/**
 * Compliance audit document sources (CUR-2555): finding documents in an organisation's connected
 * SharePoint and adding copies to a compliance audit. Shared so the server gates and the client
 * agree on the flag.
 */
import type { DocumentSourceConnectionStatus, DocumentSourceLibraryApproval } from '@shared/prisma/enums';

/**
 * Default-off PostHog rollout flag. While it is off every document sources procedure answers
 * NOT_FOUND, and the configuration card and the audit's "Find documents" action are hidden.
 */
export const ENABLE_AUDIT_DOCUMENT_SOURCES_FLAG = 'enable-audit-document-sources';

/**
 * Why a connection needs attention (`DocumentSourceConnection.statusReason`), in the order a test
 * reports them: the first that applies is the one stored.
 *
 * - `CONSENT_MISSING`: the tenancy's admin has not accepted the consent link, or withdrew it.
 * - `CERTIFICATE_REJECTED`: Microsoft refused Curium's certificate (expired or replaced).
 * - `TENANT_NOT_FOUND`: no tenancy has the connection's tenant ID any more.
 * - `SIGN_IN_FAILED`: sign-in failed for another reason.
 * - `PERMISSION_MISSING`: signed in, but the token does not hold `Sites.Selected`.
 * - `NO_SITES`: no site has been added to the connection.
 * - `SITE_UNREACHABLE`: a site Curium was granted can no longer be opened.
 * - `SITE_LISTING_INCOMPLETE`: the last test could not list everything under a granted site (Microsoft
 *   refused a subsite or its libraries), so approvals on it wait.
 * - `APPROVED_LIBRARY_MISSING`: an approved library is no longer on its site.
 * - `LIBRARY_APPEARED`: a granted site has a library that was not there when its libraries were approved.
 * - `NO_APPROVED_LIBRARIES`: nothing is approved yet, so there is nothing Curium may search.
 */
export const DOCUMENT_SOURCE_STATUS_REASONS = [
    'CONSENT_MISSING',
    'CERTIFICATE_REJECTED',
    'TENANT_NOT_FOUND',
    'SIGN_IN_FAILED',
    'PERMISSION_MISSING',
    'NO_SITES',
    'SITE_UNREACHABLE',
    'SITE_LISTING_INCOMPLETE',
    'APPROVED_LIBRARY_MISSING',
    'LIBRARY_APPEARED',
    'NO_APPROVED_LIBRARIES',
] as const;

export type DocumentSourceStatusReason = (typeof DOCUMENT_SOURCE_STATUS_REASONS)[number];

/** How History names each reason a connection needs attention (CUR-2565). */
export const DOCUMENT_SOURCE_STATUS_REASON_LABELS = {
    CONSENT_MISSING: 'Consent not given',
    CERTIFICATE_REJECTED: 'Certificate refused',
    TENANT_NOT_FOUND: 'Tenancy not found',
    SIGN_IN_FAILED: 'Sign-in failed',
    PERMISSION_MISSING: 'Permission missing',
    NO_SITES: 'No sites',
    SITE_UNREACHABLE: 'A site can no longer be opened',
    SITE_LISTING_INCOMPLETE: 'A site could not be listed in full',
    APPROVED_LIBRARY_MISSING: 'An approved library is missing',
    LIBRARY_APPEARED: 'A library appeared after approval',
    NO_APPROVED_LIBRARIES: 'No approved libraries',
} as const satisfies Record<DocumentSourceStatusReason, string>;

/** How History names a connection's status and a library's approval (CUR-2565). */
export const DOCUMENT_SOURCE_CONNECTION_STATUS_LABELS = {
    SETTING_UP: 'Setting up',
    CONNECTED: 'Connected',
    NEEDS_ATTENTION: 'Needs attention',
    DISABLED: 'Disabled',
} as const satisfies Record<DocumentSourceConnectionStatus, string>;

export const DOCUMENT_SOURCE_LIBRARY_APPROVAL_LABELS = {
    APPROVED: 'Approved',
    NOT_APPROVED: 'Not approved',
} as const satisfies Record<DocumentSourceLibraryApproval, string>;

/** The reasons a sign-in decides; the others follow from the sites and libraries Curium holds. */
export const DOCUMENT_SOURCE_SIGN_IN_REASONS = [
    'CONSENT_MISSING',
    'CERTIFICATE_REJECTED',
    'TENANT_NOT_FOUND',
    'SIGN_IN_FAILED',
    'PERMISSION_MISSING',
] as const satisfies readonly DocumentSourceStatusReason[];

/**
 * Below a granted site, Curium lists subsites this deep, and at most this many in all (CUR-2625). A
 * site with more is listed as far as these, and the page says the rest are not listed.
 */
export const DOCUMENT_SOURCE_MAX_SUBSITE_DEPTH = 3;
export const DOCUMENT_SOURCE_MAX_SUBSITES = 50;

/** At most this many sites are added to a connection in one request. */
export const DOCUMENT_SOURCE_MAX_SITES_PER_REQUEST = 20;

/** At most this many candidate decisions in one request; a search finds far fewer. */
export const DOCUMENT_SOURCE_MAX_CANDIDATE_DECISIONS = 500;

/** A search names at most this many libraries; the officer chooses which when a connection has more. */
export const DOCUMENT_SOURCE_MAX_COLLECTION_LIBRARIES = 50;

/** A search takes at most this many terms of the officer's own, each at most this long. */
export const DOCUMENT_SOURCE_MAX_EXTRA_TERMS = 10;
export const DOCUMENT_SOURCE_MAX_EXTRA_TERM_LENGTH = 100;

/**
 * At most this many candidates are kept from one search, the suggested first; a library that lost
 * any to the limit is reported as cut short. One library's crawl reads at most as many items.
 */
export const DOCUMENT_SOURCE_MAX_COLLECTION_CANDIDATES = 2000;
